/**
 * Stytch OIDC Creation Hook
 *
 * This hook provides functionality for creating OIDC (OpenID Connect)
 * authentication using the Stytch B2B headless client.
 */
import { useCallback } from 'react';
import { getStytchClient } from '../../../../adapters/react/client/headless-client';
export function useStytchCreateOIDCConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            const result = await stytch.sso.oidc.createConnection({
                display_name: data.displayName,
                identity_provider: data.identityProvider,
            });
            return {
                success: true,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to create OIDC connection',
                data: error,
            };
        }
    }, []);
}
export function useStytchUpdateOIDCConnection() {
    return useCallback(async (data) => {
        const stytch = getStytchClient();
        if (!stytch) {
            return {
                success: false,
                error: 'Stytch client not ready',
                data: null,
            };
        }
        try {
            // CRITICAL: Only include fields that are explicitly provided to prevent
            // accidentally clearing existing SSO configuration. Stytch interprets
            // undefined fields as "clear this value" rather than "don't change".
            const updatePayload = {
                connection_id: data.connectionId,
            };
            if (data.displayName !== undefined) {
                updatePayload.display_name = data.displayName;
            }
            if (data.identityProvider !== undefined) {
                updatePayload.identity_provider = data.identityProvider;
            }
            if (data.issuer !== undefined) {
                updatePayload.issuer = data.issuer;
            }
            if (data.clientId !== undefined) {
                updatePayload.client_id = data.clientId;
            }
            if (data.clientSecret !== undefined) {
                updatePayload.client_secret = data.clientSecret;
            }
            if (data.authorizationUrl !== undefined) {
                updatePayload.authorization_url = data.authorizationUrl;
            }
            if (data.tokenUrl !== undefined) {
                updatePayload.token_url = data.tokenUrl;
            }
            if (data.userinfoUrl !== undefined) {
                updatePayload.userinfo_url = data.userinfoUrl;
            }
            if (data.jwksUrl !== undefined) {
                updatePayload.jwks_url = data.jwksUrl;
            }
            const result = await stytch.sso.oidc.updateConnection(updatePayload);
            return {
                success: true,
                requestId: result.request_id,
                statusCode: result.status_code,
                connection: result.connection,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to update OIDC connection',
                data: error,
            };
        }
    }, []);
}
